import { useFeatureFlag, FEATURE_FLAGS } from '@/lib/feature-flags';
import { useCanMutate, useIsReadOnly } from './hooks';

/**
 * Tooltip copy for meeting-capture controls a read-only user can't use
 * (shown by MutationGate when useCanUseMeetingCapture() is false).
 */
export const READ_ONLY_MEETINGS_REASON =
  "Read-only role can't record or share meetings in this workspace.";

/**
 * Whether the current user may use meeting capture: connect email/calendar,
 * configure the recorder, download the desktop app, toggle meeting recording,
 * and create clips. Share links are separate — see useCanShareMeeting.
 *
 * Full users (member/admin/owner) always can. Read-only users can only when
 * the workspace has the `read-only-meetings` flag on — the backend enforces the
 * same preferences key, so this gate mirrors server behavior rather than
 * replacing it.
 */
export const useCanUseMeetingCapture = (): boolean => {
  const canMutate = useCanMutate();
  const isReadOnly = useIsReadOnly();
  const readOnlyMeetingsEnabled = useFeatureFlag(FEATURE_FLAGS.READ_ONLY_MEETINGS);
  return canMutate || (isReadOnly && readOnlyMeetingsEnabled);
};

/**
 * Whether the current user may create a share link for a recording/transcript.
 * Any workspace member can, read-only seats included (no `read-only-meetings`
 * flag needed): a share link writes no CRM data. The backend still limits a
 * read-only caller to meetings they own or attended.
 */
export const useCanShareMeeting = (): boolean => {
  const canMutate = useCanMutate();
  const isReadOnly = useIsReadOnly();
  return canMutate || isReadOnly;
};
